import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anon, object = user
  const [user, setUser] = useState(null);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem("yoles_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // Only clear the session on a real auth failure (401/403).
      // Network hiccups (timeout, 5xx, CORS glitch) should NOT force logout —
      // that used to cause the "page appears then disappears" symptom.
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("yoles_token");
        setUser(false);
      } else {
        // Keep the token; mark session as anonymous only until next successful ping.
        // Better UX: retry once after a short delay.
        setTimeout(async () => {
          try {
            const { data } = await api.get("/auth/me");
            setUser(data);
          } catch (err2) {
            const s2 = err2?.response?.status;
            if (s2 === 401 || s2 === 403) {
              localStorage.removeItem("yoles_token");
              setUser(false);
            } else {
              setUser(false); // give up gracefully; user can log in again
            }
          }
        }, 1200);
      }
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("yoles_token", data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const register = async (email, password, name, leagueCode, favoriteYoleId, antiBot = {}) => {
    try {
      const payload = { email, password, name };
      if (leagueCode) payload.league_code = leagueCode;
      if (favoriteYoleId) payload.favorite_yole_id = favoriteYoleId;
      if (antiBot.hp !== undefined) payload.hp = antiBot.hp;
      if (antiBot.elapsed_ms !== undefined) payload.elapsed_ms = antiBot.elapsed_ms;
      const { data } = await api.post("/auth/register", payload);
      localStorage.setItem("yoles_token", data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = () => {
    localStorage.removeItem("yoles_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
